"use client";

import logger from "@/utils/logger";
import { isBugsnagEnabled } from "@/lib/bugsnag";

import React, { useEffect, useState, useCallback } from "react";
import { Button } from "react-bootstrap";
import { useAuth } from "@/context/auth-context";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { useTheme } from "next-themes";
import DepartmentSelectionModal from "@/components/ui/DepartmentSelectionModal";
import AppModal from "@/components/ui/Modal";
import AuthTransitionLoader from "@/components/auth/AuthTransitionLoader";
import {
    hasRecoverableAuthHints,
    readAuthHintSnapshot,
} from "@/components/auth/authRecovery";
import { isDriverUser } from "@/utils/roleUtils";
import { consumeStoredAuthRedirectNotice } from "@/services/accountAccessState";

export default function Login() {
    const {
        login,
        isAuthenticated,
        isLoading,
        isProcessingRedirect, // 🔥 NEW: Use auth context's redirect processing flag
        userProfile,
        externalUserProfile,
        loginType,
        refreshUserProfile,
        authError,
        clearAuthError,
    } = useAuth();
    const router = useRouter();
    const { resolvedTheme } = useTheme();

    const [authLoading, setAuthLoading] = useState(false);
    const [showDepartmentModal, setShowDepartmentModal] = useState(false);
    const [progressText, setProgressText] = useState("Loading...");
    const [shouldShowLogin, setShouldShowLogin] = useState(false);
    const [forceShowLogin, setForceShowLogin] = useState(false);
    const [redirectNotice, setRedirectNotice] = useState<string | null>(null);

    const hasRecoverableStoredAuth = useCallback(() => {
        if (typeof window === "undefined") {
            return false;
        }

        return hasRecoverableAuthHints(
            readAuthHintSnapshot(window.localStorage, false)
        );
    }, []);

    // One-time client log to confirm whether Bugsnag reporting is enabled
    useEffect(() => {
        const enabled = isBugsnagEnabled();
        logger.info(`Bug reporting is ${enabled ? "ENABLED" : "DISABLED"}`, {
            bugsnagEnabled: enabled,
            env: process.env.NEXT_PUBLIC_ENV,
            releaseStage: process.env.NEXT_PUBLIC_BUGSNAG_RELEASE_STAGE,
            isAPIKeyPresent: !!process.env.NEXT_PUBLIC_BUGSNAG_API_KEY,
        });
    }, []);

    useEffect(() => {
        const notice = consumeStoredAuthRedirectNotice("/");
        if (notice?.message) {
            setRedirectNotice(notice.message);
        }
    }, []);

    // Better detection for auth state
    const [isProcessingAuth, setIsProcessingAuth] = useState(() => {
        if (typeof window !== "undefined") {
            const authInProgress = sessionStorage.getItem("auth_in_progress");
            const hasAuthParams =
                window.location.search.includes("code=") ||
                window.location.search.includes("state=");
            return !!(authInProgress || hasAuthParams);
        }
        return false;
    });

    // Monitor URL changes to detect navigation
    useEffect(() => {
        const handleUrlChange = () => {
            const hasAuthParams =
                window.location.search.includes("code=") ||
                window.location.search.includes("state=");
            const authInProgress = sessionStorage.getItem("auth_in_progress");

            logger.info("📍 URL Change detected:", {
                hasAuthParams,
                authInProgress,
                url: window.location.pathname,
            });

            // If we're back on the home page without auth params, stop processing
            if (
                window.location.pathname === "/" &&
                !hasAuthParams &&
                !authInProgress
            ) {
                logger.info(
                    "🔙 Back on login page without auth params, resetting"
                );
                setIsProcessingAuth(false);
                setAuthLoading(false);
                setShouldShowLogin(true);
                setForceShowLogin(true);
            }
        };

        window.addEventListener("popstate", handleUrlChange);

        return () => {
            window.removeEventListener("popstate", handleUrlChange);
        };
    }, []);

    // Check if user might already be authenticated on mount
    const [likelyAuthenticated, setLikelyAuthenticated] = useState(() => {
        return hasRecoverableStoredAuth();
    });

    // Initialize auth state on mount
    useEffect(() => {
        if (isProcessingAuth) {
            logger.info("🔍 Auth processing detected on mount");
            setAuthLoading(true);
            setProgressText("Processing authentication...");
        }
    }, [isProcessingAuth]);

    // More aggressive auth check with better logout detection
    useEffect(() => {
        if (!isLoading) {
            logger.info("🔍 Auth loading complete, making decision...", {
                isAuthenticated,
                likelyAuthenticated,
                isProcessingAuth,
                authLoading,
                forceShowLogin,
            });

            if (isAuthenticated) {
                // User is definitely authenticated
                setShouldShowLogin(false);
                setForceShowLogin(false);
                setLikelyAuthenticated(true);
            } else {
                // User is not authenticated
                if (forceShowLogin) {
                    // Timeout forced us to show login
                    setShouldShowLogin(true);
                } else if (!isProcessingAuth && !authLoading) {
                    // Check if we should show login
                    if (!hasRecoverableStoredAuth()) {
                        // No auth data found, user likely logged out
                        logger.info("🔍 No auth data found, showing login");
                        setLikelyAuthenticated(false);
                        setShouldShowLogin(true);
                    } else if (likelyAuthenticated) {
                        // Give some time for auth context to process
                        const timer = setTimeout(() => {
                            if (
                                !isAuthenticated &&
                                !isProcessingAuth &&
                                !authLoading
                            ) {
                                logger.info(
                                    "🔍 Auth context finished, but user not authenticated - showing login"
                                );
                                setLikelyAuthenticated(false);
                                setShouldShowLogin(true);
                            }
                        }, 1000);
                        return () => clearTimeout(timer);
                    } else {
                        // Show login immediately
                        setShouldShowLogin(true);
                    }
                }
            }
        }
    }, [
        isLoading,
        isAuthenticated,
        isProcessingAuth,
        authLoading,
        likelyAuthenticated,
        forceShowLogin,
        hasRecoverableStoredAuth,
    ]);

    // Force show login after a timeout to prevent infinite loading
    useEffect(() => {
        // 🔥 FIX: Check if we're actively processing a redirect (code= in URL)
        // If so, don't start the timeout - let the auth flow complete
        const hasAuthCode =
            typeof window !== "undefined" &&
            (window.location.search.includes("code=") ||
                window.location.hash.includes("code="));

        if (hasAuthCode) {
            logger.info("🔍 Auth code detected in URL, skipping timeout");
            return; // Don't set any timeout while redirect is being processed
        }

        // If we're in "processing" state but not actually authenticated, force show login
        // This handles the case where user clicks back from auth page
        if (isProcessingAuth && !isAuthenticated && !isLoading) {
            const timeout = setTimeout(() => {
                // Double-check we still don't have auth code (it may have been cleared)
                const stillHasCode =
                    window.location.search.includes("code=") ||
                    window.location.hash.includes("code=");
                if (stillHasCode) {
                    logger.info("🔍 Auth code still in URL, extending wait...");
                    return;
                }

                logger.info(
                    "⚠️ Still in processing state but no auth - forcing login display"
                );
                sessionStorage.removeItem("auth_in_progress");
                setIsProcessingAuth(false);
                setAuthLoading(false);
                setForceShowLogin(true);
                setShouldShowLogin(true);
            }, 5000); // 🔥 Increased to 5 seconds to allow MSAL flow to complete

            return () => clearTimeout(timeout);
        }

        // Original timeout for initial page load
        if (!isAuthenticated && !authLoading && !isProcessingAuth) {
            const timeout = setTimeout(() => {
                logger.info("🚨 Timeout reached, forcing login display");
                setForceShowLogin(true);
                setShouldShowLogin(true);
                setLikelyAuthenticated(false);
            }, 3000); // 3 second safety timeout

            return () => clearTimeout(timeout);
        }
    }, [isAuthenticated, authLoading, isProcessingAuth, isLoading]);

    // Text animation for modal
    useEffect(() => {
        if (authLoading || isProcessingAuth) {
            const progressSteps = [
                "Connecting to NYBCe...",
                "Verifying credentials...",
                "Loading your profile...",
                "Setting up dashboard...",
                "Welcome! Taking you to dashboard...",
            ];

            let currentStep = isProcessingAuth ? 1 : 0;

            const interval = setInterval(() => {
                if (currentStep < progressSteps.length) {
                    setProgressText(progressSteps[currentStep]);
                    currentStep++;
                } else {
                    clearInterval(interval);
                }
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [authLoading, isProcessingAuth]);

    // Handle login click
    const handleLogin = useCallback(() => {
        logger.info("🔍 Starting MSAL login");
        setAuthLoading(true);
        setIsProcessingAuth(true);
        setShouldShowLogin(false);
        setForceShowLogin(false);
        setProgressText("Loading...");

        sessionStorage.setItem("auth_in_progress", "true");

        setTimeout(() => {
            login();
        }, 500);
    }, [login]);

    // Monitor for auth completion and redirect authenticated users
    useEffect(() => {
        if (isAuthenticated) {
            logger.info("🔍 User is authenticated, checking profile...");

            sessionStorage.removeItem("auth_in_progress");
            setShouldShowLogin(false);
            setForceShowLogin(false);

            // Handle MSAL users
            if (loginType === "msal" && userProfile) {
                // 🔥 FIX: Check if user is a driver - don't redirect them, let DriverAccessBlock handle it
                const userIsDriver = isDriverUser(userProfile.roles);

                if (userIsDriver) {
                    logger.info(
                        "🚫 Driver user detected - not redirecting, DriverAccessBlock will handle"
                    );
                    setAuthLoading(false);
                    setIsProcessingAuth(false);
                    // Don't redirect - let DriverAccessBlock component show the message
                    return;
                }

                const needsDepartment =
                    userProfile.organizationId && !userProfile.departmentId;

                if (needsDepartment) {
                    logger.info("🔍 User needs department selection");
                    setAuthLoading(false);
                    setIsProcessingAuth(false);
                    setShowDepartmentModal(true);
                } else {
                    logger.info(
                        "🔍 User has department, redirecting to dashboard"
                    );
                    // 🔥 FIX: Clear processing state and redirect immediately
                    setAuthLoading(false);
                    setIsProcessingAuth(false);
                    setProgressText("Welcome! Taking you to dashboard...");
                    // Use short timeout just for UX, not for waiting
                    setTimeout(() => {
                        router.replace("/dashboard");
                    }, 200);
                }
            }
            // Handle External users
            else if (loginType === "external" && externalUserProfile) {
                // 🔥 FIX: Check if user is a driver - don't redirect them, let DriverAccessBlock handle it
                const userIsDriver = isDriverUser(externalUserProfile.roles);

                if (userIsDriver) {
                    logger.info(
                        "🚫 Driver user detected - not redirecting, DriverAccessBlock will handle"
                    );
                    setAuthLoading(false);
                    setIsProcessingAuth(false);
                    // Don't redirect - let DriverAccessBlock component show the message
                    return;
                }

                logger.info(
                    "🔍 External user authenticated, redirecting to external dashboard"
                );
                // 🔥 FIX: Clear processing state and redirect immediately
                setAuthLoading(false);
                setIsProcessingAuth(false);
                setProgressText("Welcome! Taking you to dashboard...");
                setTimeout(() => {
                    router.replace("/externalLoginDashboard");
                }, 200);
            }
        }
    }, [
        isAuthenticated,
        isLoading,
        userProfile,
        externalUserProfile,
        loginType,
        router,
        authLoading,
        isProcessingAuth,
    ]);

    // Handle department selection
    const handleDepartmentSelected = async () => {
        setShowDepartmentModal(false);
        setAuthLoading(true);
        setIsProcessingAuth(true);
        setProgressText("Updating your profile...");

        try {
            await refreshUserProfile();
            setProgressText("Setup complete! Redirecting...");
            setTimeout(() => {
                router.replace("/dashboard");
            }, 1000);
        } catch (error) {
            console.error("Error refreshing profile:", error);
            router.replace("/dashboard");
        }
    };

    // Check if we should show the department modal
    const shouldShowDepartmentModal =
        showDepartmentModal &&
        userProfile?.organizationId &&
        userProfile?.organization &&
        userProfile?.email;

    // Show modal when authenticating OR processing auth
    const showModal = authLoading || isProcessingAuth;

    // 🔥 FIX: More robust loading state logic
    // Show loading if:
    // 1. Auth context is still loading
    // 2. We're processing MSAL auth (indicated by isProcessingAuth, isProcessingRedirect, OR sessionStorage flag)
    // 3. We have auth data in storage and auth context hasn't finished yet
    // 4. User is authenticated and waiting for redirect
    const showLoadingState =
        isLoading ||
        isProcessingAuth ||
        isProcessingRedirect || // 🔥 NEW: Auth context is processing redirect
        (likelyAuthenticated &&
            !isAuthenticated &&
            !shouldShowLogin &&
            !forceShowLogin) ||
        (isAuthenticated && !showModal && !shouldShowDepartmentModal);

    logger.info("🔍 Login Page State:", {
        isAuthenticated,
        isLoading,
        isProcessingRedirect,
        authLoading,
        isProcessingAuth,
        shouldShowLogin,
        forceShowLogin,
        likelyAuthenticated,
        showModal,
        showLoadingState,
        progressText,
    });

    // 🔥 FIX: Show loading state while processing auth - this takes priority over shouldShowLogin
    // When isProcessingAuth OR isProcessingRedirect is true, ALWAYS show loading, never show login buttons
    if (
        isProcessingAuth ||
        isProcessingRedirect ||
        (showLoadingState && !shouldShowLogin && !forceShowLogin)
    ) {
        return (
            <AuthTransitionLoader
                testId="internal-login-transition-loader"
                message="Loading..."
                progressText={progressText}
            />
        );
    }

    return (
        <>
            <div className="login-container">
                <div className="login-content">
                    <div className="logo-wrapper">
                        <Image
                            src={
                                resolvedTheme === "dark"
                                    ? "/logo-dark.png"
                                    : "/logo.png"
                            }
                            alt="Dispatch-IQ"
                            width={160}
                            height={160}
                            style={{ objectFit: "contain" }}
                            priority
                        />
                    </div>

                    {/* Error Display */}
                    {(authError || redirectNotice) && !isAuthenticated && (
                        <div className="auth-error-container">
                            <div
                                className="alert alert-danger d-flex align-items-center"
                                role="alert"
                            >
                                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                <div className="flex-grow-1">
                                    {authError || redirectNotice}
                                </div>
                                <button
                                    type="button"
                                    className="btn-close ms-2"
                                    onClick={() => {
                                        clearAuthError();
                                        setRedirectNotice(null);
                                    }}
                                    aria-label="Close"
                                ></button>
                            </div>
                        </div>
                    )}

                    {/* Show login buttons when appropriate - NEVER during auth processing */}
                    {(shouldShowLogin || forceShowLogin) &&
                        !isAuthenticated &&
                        !isProcessingAuth &&
                        !isProcessingRedirect && // 🔥 NEW: Also check context redirect flag
                        !isLoading &&
                        !(authError || redirectNotice) && (
                            <div className="login-buttons">
                                <Button
                                    variant="outline-primary"
                                    size="lg"
                                    className="login-btn"
                                    onClick={handleLogin}
                                    disabled={false}
                                >
                                    NYBCe
                                </Button>
                                <Button
                                    variant="outline-primary"
                                    size="lg"
                                    className="login-btn"
                                    onClick={() =>
                                        router.push("/externalLogin")
                                    }
                                    disabled={false}
                                >
                                    External
                                </Button>
                            </div>
                        )}
                </div>
            </div>

            <style jsx>{`
                .auth-error-container {
                    margin-top: 1.5rem;
                    width: 100%;
                    max-width: 400px;
                }
            `}</style>

            {/* Authentication Modal - Always show when processing */}
            {showModal && (
                <AppModal
                    show={true}
                    title="Authenticating"
                    loading
                    spinnerText={progressText}
                />
            )}

            {/* Department Modal */}
            {shouldShowDepartmentModal && (
                <DepartmentSelectionModal
                    show={showDepartmentModal}
                    organizationId={userProfile!.organizationId!}
                    organizationName={userProfile!.organization!}
                    userEmail={userProfile!.email!}
                    onDepartmentSelected={handleDepartmentSelected}
                    onError={() => {
                        setShowDepartmentModal(false);
                        router.replace("/dashboard");
                    }}
                />
            )}
        </>
    );
}
